import { mapActions, mapState } from 'vuex'
import * as R from 'ramda'
import { isNilOrEmpty } from 'ramda-adjunct'
import { GET, DELETE } from 'constants/api'
import { toSingular, getApiParams, pascalToCamelCase } from 'utils/helpers'
import * as DEFAULT_API_PARAMS from 'constants/defaultApiParams'
import { METADATA_SUFFIX } from '../constants/api'
import { showDeleteDialog } from '../utils/dialog'
import { showErrorWithTimeout } from '../utils/form'
import { camelToPascalCase } from '../utils/helpers'

export default ({
  apiMethodsSuffix,
  parentRouteName,
  storeModuleName,
  storeItemName,
  enums,
  enumsMap,
  preventDelete,
  preventDetail,
  preventCreate,
  preventFetchOnCreated,
  detailRouteName,
  additionalParams,
  includeParamRouteId,
  preventSaveParams
}) => {
  const getDetailRoute = vm => R.isNil(detailRouteName) ? toSingular(vm.$route.name) : detailRouteName

  const moduleNameUppercase = R.toUpper(apiMethodsSuffix)

  const defaultParams = DEFAULT_API_PARAMS[moduleNameUppercase] || {}

  const moduleName = R.isNil(storeModuleName)
    ? pascalToCamelCase(apiMethodsSuffix)
    : storeModuleName

  const itemName = R.isNil(storeItemName)
    ? moduleName
    : storeItemName

  const detailName = R.isNil(detailRouteName)
    ? toSingular(apiMethodsSuffix)
    : camelToPascalCase(detailRouteName)

  const stateToMap = {
    items: state => state[moduleName][itemName],
    itemsMetadata: state => state[moduleName][itemName + METADATA_SUFFIX],
    pending: state => state[moduleName].pending[itemName],
    savedParams: state => state.layout.params[moduleNameUppercase]
  }

  if (enums) {
    stateToMap.enums = state => state.enums.enums
  }

  if (enumsMap) {
    stateToMap.enumsMap = state => state.enums.enumsMap
  }

  const apiMethods = [
    GET + apiMethodsSuffix,
    'setParams'
  ]

  const conditionalMethods = {}

  if (!preventDelete) {
    const deleteApiMethod = DELETE + detailName
    const getApiMethod = GET + apiMethodsSuffix

    apiMethods.push(deleteApiMethod)

    conditionalMethods.deleteItem = async function (item) {
      const deleteError = this.getDeleteError(item)
      if (deleteError) {
        showErrorWithTimeout(this, deleteError)
      } else if (this.shouldConfirmDelete(item)) {
        showDeleteDialog(this, {
          apiMethod: deleteApiMethod,
          params: {
            id: item._id
          },
          shouldRefresh: this.shouldRefreshAfterDelete(),
          refresh: {
            apiMethod: getApiMethod,
            params: getApiParams(defaultParams, this.params)
          }
        }, this.getDeleteMessage(item))
      } else {
        this[deleteApiMethod]({ params: { id: item._id } })
      }
    }
  }

  if (!preventDetail) {
    conditionalMethods.goToDetail = function (item) {
      this.$router.push({
        name: getDetailRoute(this),
        params: { id: item._id }
      })
    }
  }
  if (!preventCreate) {
    conditionalMethods.createItem = function () {
      this.$router.push({
        name: getDetailRoute(this),
        params: { id: 'new' }
      })
    }
  }

  return {
    created() {
      this.initParams()
      if (!preventFetchOnCreated && this.shouldRefreshOnCreate()) {
        this.fetchItems()
      }
      this.setupPage()
    },
    data() {
      return {
        listPage: 1,
        params: {
          ...defaultParams
        }
      }
    },
    computed: {
      ...mapState(
        stateToMap
      ),
      listLoading() {
        return this.pending && isNilOrEmpty(this.items)
      }
    },
    methods: {
      ...mapActions(apiMethods),
      ...conditionalMethods,
      fetchItems(resetPage) {
        if (resetPage && this.listPage !== 1) {
          this.tablePageChanged(1)
          return
        }

        if (additionalParams) {
          for (var property in additionalParams) {
            this.params[property] = additionalParams[property]
          }
        }

        if (includeParamRouteId) {
          this.params['id'] = this.$route.params.id
        }

        const params = getApiParams(defaultParams, this.params)

        return this[GET + apiMethodsSuffix]({
          params
        })
      },
      sortItems({ order }) {
        const orderChanged = this.params.order !== order

        this.params.order = order

        if (orderChanged) {
          this.params.offset = 0
          this.listPage = 1
          this.fetchItems()
        }
      },
      tablePageChanged(page, fetchItems = true) {
        window.scrollTo(0, 0)
        this.listPage = page
        this.params.offset = (this.listPage - 1) * this.params.limit
        if (fetchItems) {
          this.fetchItems()
        }
      },
      setupPage() {
        this.listPage = this.params.offset / this.params.limit + 1
      },
      getDeleteMessage() {
        return null
      },
      getDeleteError() {
        return null
      },
      shouldConfirmDelete() {
        return true
      },
      shouldRefreshAfterDelete() {
        return false
      },
      shouldRefreshOnCreate() {
        return true
      },
      initParams() {
        if (this.savedParams && !preventSaveParams) {
          this.params = this.savedParams
        }
        this.saveParams()
      },
      saveParams() {
        if (!preventSaveParams) {
          this.setParams({
            agendaParams: { ...this.params },
            agendaName: moduleNameUppercase
          })
        }
      }
    },
    watch: {
      params: {
        handler() {
          this.saveParams()
        },
        deep: true
      }
    }
  }
}
